import { computed, ref, watchEffect, type ComputedRef, type Ref } from 'vue';
import type { IntegrationSystemOption, IntegrationConfigValues } from '@/js/services/api/backOffice/types';

// Populates form values from the system's field definitions plus any existing config, and
// validates required fields — shared between the generic integration-config page and each
// system's dedicated config page (e.g. Genesys), which otherwise duplicate this logic.
export function useIntegrationConfigForm(
    selectedTenantId: Ref<string>,
    system: ComputedRef<IntegrationSystemOption | null>,
    existingConfigData: Ref<IntegrationConfigValues | undefined>,
    isConfigExisting: ComputedRef<boolean>
) {
    const formValues = ref<Record<string, string>>({});

    watchEffect(() => {
        if (!system.value || !selectedTenantId.value) {
            formValues.value = {};
            return;
        }

        const existing = existingConfigData.value?.fieldValues ?? {};
        formValues.value = Object.fromEntries(
            system.value.fields.map((f) => [f.key, existing[f.key] ?? f.defaultValue ?? ''])
        );
    });

    const canSubmit = computed(() => {
        if (!selectedTenantId.value || !system.value) return false;
        return system.value.fields
            .filter((f) => f.required)
            .every((f) => {
                if (f.type === 'password' && isConfigExisting.value) return true;
                return !!formValues.value[f.key];
            });
    });

    return { formValues, canSubmit };
}
